import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import H from '@here/maps-api-for-javascript';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  compoundMap,
  CompoundOccupancyError,
  CompoundRequestError,
  validateSnapshot,
} from '@/api/CommandCentre/CompoundMap';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useAppCustomers } from '@/hooks/useAppCustomers';
import { useIdTokenClaims } from '@/hooks/useIdTokenClaims';
import {
  CompoundMetadata,
  CompoundSlot,
  CompoundSlots,
} from '@/types/CompoundMap';
import { CompoundMapAdapter } from './CompoundMapAdapter';
import { CompoundTooltip } from '@/modules/commandCentre/compound/CompoundTooltip';
import { useRecordedOccupancy } from './useRecordedOccupancy';
import { useCompoundMapRollout } from '@/modules/commandCentre/compound/useCompoundMapRollout';
import {
  RecordedVehicleDetails,
  recordedStatus,
} from './RecordedVehicleDetails';
import {
  DemoOccupancy,
  featureId,
  isMapped,
  searchSlots,
  slotLabel,
} from './spatial';
import './CompoundPreview.scss';

type Props = {
  map: H.Map | null;
  siteId?: number;
  onActiveChange: (active: boolean) => void;
};
type ScopedProps = Props & {
  scope: string;
  currentSite: React.RefObject<number | undefined>;
};
type PreviewProps = ScopedProps & { restoreCamera: () => void };

function captureCamera({
  map,
  siteId,
  currentSite,
}: Pick<ScopedProps, 'map' | 'siteId' | 'currentSite'>) {
  if (!map) return () => {};
  const view = map.getViewModel().getLookAtData();
  const camera = {
    position: view.position
      ? { lat: view.position.lat, lng: view.position.lng }
      : undefined,
    zoom: view.zoom,
    heading: view.heading ?? 0,
    tilt: view.tilt ?? 0,
  };
  // Capture when an eligible session mounts; check the live Site when leaving.
  return () =>
    map
      .getViewModel()
      .setLookAtData(
        currentSite.current === siteId
          ? camera
          : { heading: camera.heading, tilt: camera.tilt },
        false,
      );
}

/** The master switch allows discovery; Compound decides which Site map is published. */
export function CompoundPreview(props: Props) {
  const eligible = useCompoundMapRollout();
  const { customerIds, locationIds, userPermissions, hasGbaAccess } =
    useAppCustomers();
  const { accounts } = useIdTokenClaims();
  const currentSite = useRef(props.siteId);
  currentSite.current = props.siteId;
  const scope = JSON.stringify([
    accounts[0]?.homeAccountId,
    accounts[0]?.localAccountId,
    [...customerIds].sort(),
    [...locationIds].sort(),
    userPermissions,
    props.siteId,
  ]);
  if (
    !eligible ||
    !hasGbaAccess ||
    !props.siteId ||
    !Number.isSafeInteger(props.siteId) ||
    props.siteId <= 0 ||
    !accounts.length ||
    !props.map
  )
    return null;
  return (
    <CompoundPreviewSession
      key={scope}
      {...props}
      scope={scope}
      currentSite={currentSite}
    />
  );
}

/** Remounted for a new user/site/customer/permission scope: no previous state survives. */
export function CompoundPreviewSession(props: ScopedProps) {
  const { map, siteId, currentSite, onActiveChange } = props;
  const [restoreCamera, setRestoreCamera] = useState<(() => void) | null>(null);
  useLayoutEffect(() => {
    // Previous scope cleanup restores legacy before this session captures its camera.
    setRestoreCamera(() => captureCamera({ map, siteId, currentSite }));
  }, [map, siteId, currentSite]);
  useLayoutEffect(() => {
    onActiveChange(false);
    return () => onActiveChange(false);
  }, [onActiveChange]);
  return restoreCamera ? (
    <LoadPreview {...props} restoreCamera={restoreCamera} />
  ) : null;
}

function MapSettings({ children }: React.PropsWithChildren) {
  return (
    <details className="cc-compound" aria-label="Candidate compound map">
      <summary
        className="cc-compound__summary"
        aria-label="Map settings"
        title="Map settings"
      >
        <FontAwesomeIcon icon={faGear} aria-hidden="true" />
      </summary>
      <div className="cc-compound__settings">{children}</div>
    </details>
  );
}

function LoadPreview(props: PreviewProps) {
  const [fatalError, setFatalError] = useState<Error | null>(null);
  if (fatalError)
    return (
      <div role="alert">
        {fatalError.message}{' '}
        <button type="button" onClick={() => setFatalError(null)}>
          Retry preview
        </button>
      </div>
    );
  // A fatal CAD response unmounts the whole attempt: its layers, local simulation,
  // selection, requests and query data end together before retry reads a new manifest.
  return <PreviewAttempt {...props} onFatalError={setFatalError} />;
}

function PreviewAttempt({
  map,
  siteId,
  scope,
  onActiveChange,
  restoreCamera,
  onFatalError,
}: PreviewProps & { onFatalError: (error: Error) => void }) {
  const client = useQueryClient();
  const [attempt, setAttempt] = useState(0);
  const queryKey = useMemo(
    () => [QUERY_KEYS.compoundMap, scope, attempt],
    [scope, attempt],
  );
  const metadataQuery = useQuery({
    queryKey: [...queryKey, 'metadata'],
    queryFn: async ({ signal }) => {
      const metadata = await compoundMap.metadata(siteId!, signal);
      if (
        metadata.siteId !== siteId ||
        !['available', 'disabled', 'no-data'].includes(metadata.availability)
      )
        throw new Error(
          'Candidate response has an invalid Site or availability.',
        );
      if (
        metadata.availability === 'available' &&
        (!metadata.datasetId ||
          !metadata.version ||
          !metadata.bounds ||
          !metadata.summary ||
          !metadata.cad)
      )
        throw new Error('Candidate snapshot is incomplete.');
      return metadata;
    },
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const metadata = metadataQuery.data;
  const slotsQuery = useQuery({
    queryKey: [
      ...queryKey,
      metadata?.datasetId,
      metadata?.version,
      metadata?.cad?.packageId,
    ],
    queryFn: async ({ signal }) =>
      validateSnapshot(metadata!, await compoundMap.slots(metadata!, signal)),
    enabled: metadata?.availability === 'available',
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  const unavailable =
    metadata?.availability === 'no-data' ||
    metadata?.availability === 'disabled';
  const error = metadataQuery.error || slotsQuery.error;
  useEffect(
    () => () => {
      void client.cancelQueries({ queryKey });
      client.removeQueries({ queryKey });
    },
    [client, queryKey],
  );

  if (error)
    return (
      <div role="alert">
        {error.message}{' '}
        <button type="button" onClick={() => setAttempt((value) => value + 1)}>
          Retry preview
        </button>
      </div>
    );
  if (unavailable || !metadata || !slotsQuery.data || !map) return null;
  return (
    <SnapshotPreview
      key={`${metadata.datasetId}:${metadata.version}:${metadata.cad!.packageId}`}
      map={map}
      metadata={metadata}
      data={slotsQuery.data}
      scope={scope}
      onFatalError={onFatalError}
      onActiveChange={onActiveChange}
      restoreCamera={restoreCamera}
    />
  );
}

function SnapshotPreview({
  map,
  metadata,
  data,
  scope,
  onFatalError,
  onActiveChange,
  restoreCamera,
}: {
  map: H.Map;
  metadata: CompoundMetadata;
  data: CompoundSlots;
  scope: string;
  onFatalError: (error: Error) => void;
  onActiveChange: (active: boolean) => void;
  restoreCamera: () => void;
}) {
  const adapter = useRef<CompoundMapAdapter | null>(null);
  const [ready, setReady] = useState(false);
  useLayoutEffect(() => {
    onActiveChange(ready);
    return () => {
      onActiveChange(false);
      if (ready) restoreCamera();
    };
  }, [ready, onActiveChange, restoreCamera]);
  const [cadError, setCadError] = useState<string>();
  const [cadRetry, setCadRetry] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [mode, setMode] = useState<'none' | 'recorded' | 'simulation'>(
    'recorded',
  );
  const simulation = mode === 'simulation';
  const recorded = useRecordedOccupancy(
    metadata,
    data,
    scope,
    mode === 'recorded',
  );
  const versionChanged =
    recorded.error instanceof CompoundOccupancyError &&
    recorded.error.status === 409;
  const [slotsVisible, setSlotsVisible] = useState(true);
  const [cadVisible, setCadVisible] = useState(true);
  const [linework, setLinework] = useState(true);
  const [opacity, setOpacity] = useState(85);
  const [term, setTerm] = useState('');
  const [resultsOpen, setResultsOpen] = useState(false);
  const vehicles = useMemo(
    () => (simulation ? DemoOccupancy.assign(data.features) : new Map()),
    [data, simulation],
  );
  const features = useMemo(
    () => new Map(data.features.map((f) => [featureId(f), f])),
    [data],
  );
  const recordedVehicles = useMemo(
    () =>
      new Map(
        data.features.map((feature) => [
          featureId(feature),
          recorded.rows.get(feature.properties.slotId)?.vehicles ?? [],
        ]),
      ),
    [data, recorded.rows],
  );
  const recordedStatuses = useMemo(
    () =>
      new Map(
        data.features.map((feature) => [
          featureId(feature),
          recorded.rows.get(feature.properties.slotId)?.status ?? 'unknown',
        ]),
      ),
    [data, recorded.rows],
  );
  const matches = useMemo(
    () =>
      searchSlots(
        data.features,
        term,
        mode === 'recorded' ? recordedVehicles : vehicles,
      ),
    [data, term, mode, vehicles, recordedVehicles],
  );
  const selectedFeature = selected ? features.get(selected) : undefined;
  const hoveredFeature = hovered ? features.get(hovered) : undefined;
  const changeMode = (next: typeof mode) => {
    setMode(next);
    setTerm('');
    setResultsOpen(false);
    setSelected(null);
    setHovered(null);
    adapter.current?.select(null);
  };
  useLayoutEffect(() => {
    if (!recorded.accessLost) return;
    setTerm('');
    setResultsOpen(false);
    setSelected(null);
    setHovered(null);
    adapter.current?.select(null);
  }, [recorded.accessLost]);

  useLayoutEffect(() => {
    let active = true;
    const instance = new CompoundMapAdapter(
      map,
      metadata,
      data,
      setSelected,
      setHovered,
      (error) => {
        if (!active) return;
        if (
          error instanceof CompoundRequestError &&
          [401, 403, 404, 409].includes(error.status)
        ) {
          onFatalError(error);
        } else {
          setCadError(
            'CAD could not be loaded. Candidate outlines remain available.',
          );
        }
      },
    );
    adapter.current = instance;
    // Exposed only inside the eligible preview; never contains identity or credentials.
    Object.defineProperty(window, '__compoundPreviewDiagnostics', {
      configurable: true,
      value: () => instance.diagnostics(),
    });
    void instance
      .prepare()
      .then(() => {
        if (active) setReady(true);
      })
      .catch(() => {
        if (active)
          onFatalError(new Error('Candidate geometry could not be rendered.'));
      });
    return () => {
      active = false;
      delete (window as Window & { __compoundPreviewDiagnostics?: unknown })
        .__compoundPreviewDiagnostics;
      instance.dispose();
      adapter.current = null;
    };
  }, [map, metadata, data, onFatalError]);
  useLayoutEffect(() => {
    if (mode === 'recorded')
      adapter.current?.setRecordedOccupancy(recordedStatuses);
    else adapter.current?.setSimulation(vehicles);
  }, [mode, vehicles, recordedStatuses]);
  useEffect(() => {
    adapter.current?.setSlotsVisible(slotsVisible);
  }, [slotsVisible]);
  const opacityRef = useRef(opacity);
  opacityRef.current = opacity;
  useEffect(() => {
    if (!ready) return; // prepare() fits the drawing before HERE requests tiles.
    setCadError(undefined);
    adapter.current?.setCad(cadVisible, opacityRef.current / 100, linework);
  }, [ready, cadVisible, linework, cadRetry]); // Opacity changes do not rebuild tile resources.
  useEffect(() => {
    adapter.current?.setCadOpacity(opacity / 100);
  }, [opacity]);
  const choose = (feature: CompoundSlot) => {
    adapter.current?.select(featureId(feature), true);
    setResultsOpen(false);
  };
  if (!ready) return null;
  return (
    <MapSettings>
      <CompoundTooltip
        map={map}
        feature={
          mode === 'recorded' && recorded.accessLost
            ? undefined
            : (selectedFeature ?? hoveredFeature)
        }
        pinned={Boolean(selectedFeature)}
        mode={mode}
        row={recorded.rows.get(
          (selectedFeature ?? hoveredFeature)?.properties.slotId ?? '',
        )}
        simulatedVehicle={vehicles.get(selected ?? hovered ?? '')}
        positionSource={recorded.snapshot?.positionSource}
        stale={recorded.stale}
        onClose={() => {
          setHovered(null);
          adapter.current?.select(null);
        }}
      />
      <div className="cc-compound__notice">
        Candidate coordinates — absolute accuracy is unknown. Independent
        control points across the site are still required. Drawing bounds are
        not a Site boundary. Historical source identities are provenance only.
        Recorded occupancy matches versioned map slots to vehicle position
        records.
      </div>
      <div className="cc-compound__controls">
        <span>
          Source mapping:{' '}
          {mode !== 'recorded' && <i className="cc-compound__swatch mapped" />}
          Mapped {metadata.summary!.mapped.toLocaleString()}{' '}
          {mode !== 'recorded' && (
            <i className="cc-compound__swatch unresolved" />
          )}
          Unresolved {metadata.summary!.unresolved.toLocaleString()} /{' '}
          {data.features.length.toLocaleString()} candidates
        </span>
        <label>
          <input
            type="checkbox"
            checked={mode === 'recorded'}
            onChange={(e) => changeMode(e.target.checked ? 'recorded' : 'none')}
          />{' '}
          Recorded vehicle occupancy
        </label>
        <label>
          <input
            type="checkbox"
            checked={simulation}
            onChange={(e) => {
              changeMode(e.target.checked ? 'simulation' : 'none');
            }}
          />{' '}
          Fictional occupancy simulation
        </label>
        {simulation && (
          <strong className="cc-compound__simulation">
            SIMULATION: {vehicles.size.toLocaleString()} occupied /{' '}
            {(data.features.length - vehicles.size).toLocaleString()} empty. No
            real stock.
          </strong>
        )}
      </div>
      {mode === 'recorded' && (
        <div className="cc-compound__occupancy" aria-live="polite">
          <strong
            className={
              recorded.snapshot?.positionSource === 'dev-test'
                ? 'cc-compound__notice'
                : undefined
            }
          >
            {recorded.snapshot?.positionSource === 'dev-test'
              ? 'DEV test positions — artificially assigned for testing'
              : 'Recorded locations, not verified physical presence'}
          </strong>
          <div className="cc-compound__controls">
            <span>
              <i className="cc-compound__swatch occupied" />
              Occupied: {recorded.counts.occupied.toLocaleString()} ·{' '}
              <i className="cc-compound__swatch unrecorded" />
              No vehicle recorded in current selection:{' '}
              {recorded.counts.unrecorded.toLocaleString()} ·{' '}
              <i className="cc-compound__swatch unknown" />
              Unknown: {recorded.counts.unknown.toLocaleString()} ·{' '}
              <i className="cc-compound__swatch conflict" />
              Conflict: {recorded.counts.conflict.toLocaleString()}
            </span>
            <button
              type="button"
              onClick={
                versionChanged
                  ? () => onFatalError(recorded.error!)
                  : recorded.refresh
              }
              disabled={recorded.fetching}
            >
              {versionChanged ? 'Reload map preview' : 'Refresh occupancy'}
            </button>
          </div>
          {!recorded.snapshot && !recorded.error && (
            <p role="status">
              Loading recorded occupancy… All slots remain unknown.
            </p>
          )}
          {recorded.snapshot && (
            <p>
              Business records queried:{' '}
              <time dateTime={recorded.snapshot.asOf}>
                {new Date(recorded.snapshot.asOf).toLocaleString()}
              </time>
              {recorded.stale ? ' · STALE — last known snapshot' : ''}
              {recorded.fetching ? ' · Refreshing…' : ''}
            </p>
          )}
          {recorded.error && (
            <p role="alert">
              {recorded.error.message}{' '}
              {recorded.snapshot
                ? 'Showing the last known snapshot.'
                : 'Recorded occupancy unavailable; all slots remain unknown.'}
            </p>
          )}
          {recorded.snapshot &&
            recorded.snapshot.coverage.unpositionedVehicles > 0 && (
              <p className="cc-compound__notice">
                {recorded.snapshot.coverage.unpositionedVehicles.toLocaleString()}{' '}
                authorized vehicle records lack a complete position. Slots
                without a matching record remain unknown; these vehicles cannot
                be placed on the map.
              </p>
            )}
          {recorded.snapshot &&
            recorded.snapshot.coverage.unmatchedVehicles > 0 && (
              <p className="cc-compound__notice">
                {recorded.snapshot.coverage.unmatchedVehicles.toLocaleString()}{' '}
                positioned vehicle records have no matching confirmed slot in
                this map.
              </p>
            )}
        </div>
      )}
      <div className="cc-compound__controls">
        <div
          className="cc-compound__search"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node))
              setResultsOpen(false);
          }}
        >
          <input
            aria-label={
              mode === 'recorded'
                ? 'Search candidate slots or recorded VIN'
                : 'Search candidate slots or simulated VIN'
            }
            placeholder={
              simulation
                ? 'Slot, review ID or simulated VIN'
                : mode === 'recorded'
                  ? 'Slot, review ID or recorded VIN'
                  : 'Slot number, label or review ID'
            }
            value={term}
            disabled={!ready}
            onChange={(e) => {
              setTerm(e.target.value);
              setResultsOpen(true);
            }}
            onFocus={() => setResultsOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches[0]) choose(matches[0]);
              if (e.key === 'Escape') setResultsOpen(false);
            }}
            aria-expanded={resultsOpen && !!term.trim()}
          />
          {resultsOpen && !!term.trim() && (
            <div className="cc-compound__results">
              {matches.slice(0, 8).map((feature) => (
                <button
                  type="button"
                  key={featureId(feature)}
                  onClick={() => choose(feature)}
                >
                  {slotLabel(feature)} · {feature.properties.reviewId}
                  <small>
                    {isMapped(feature) ? 'Mapped' : 'Unresolved'}
                    {feature.properties.drawingReference &&
                      ' · Drawing reference, not linked'}
                  </small>
                  {vehicles.has(featureId(feature)) && (
                    <small>
                      VIN {vehicles.get(featureId(feature))!.vin} · Simulated
                    </small>
                  )}
                  {mode === 'recorded' &&
                    recordedVehicles
                      .get(featureId(feature))
                      ?.map((vehicle) => (
                        <small key={vehicle.stockId}>
                          VIN {vehicle.vin} · Recorded
                        </small>
                      ))}
                </button>
              ))}
              <small>
                {matches.length
                  ? `Showing ${Math.min(8, matches.length)} of ${matches.length} matches`
                  : 'No matching candidates'}
              </small>
            </div>
          )}
        </div>
        <button
          type="button"
          disabled={!ready}
          onClick={() => adapter.current?.fit()}
        >
          Fit drawing
        </button>
        <button
          type="button"
          aria-label="Zoom candidate map out"
          disabled={!ready}
          onClick={() => adapter.current?.zoom(-1)}
        >
          −
        </button>
        <button
          type="button"
          aria-label="Zoom candidate map in"
          disabled={!ready}
          onClick={() => adapter.current?.zoom(1)}
        >
          +
        </button>
        <label>
          <input
            type="checkbox"
            checked={slotsVisible}
            onChange={(e) => setSlotsVisible(e.target.checked)}
          />{' '}
          Slots
        </label>
        <label>
          <input
            type="checkbox"
            checked={cadVisible}
            onChange={(e) => setCadVisible(e.target.checked)}
          />{' '}
          CAD
        </label>
        <label>
          <input
            type="checkbox"
            checked={linework}
            onChange={(e) => setLinework(e.target.checked)}
          />{' '}
          Transparent linework
        </label>
        <label>
          CAD opacity{' '}
          <input
            type="range"
            min="0"
            max="100"
            value={opacity}
            disabled={!cadVisible}
            onChange={(e) => setOpacity(Number(e.target.value))}
          />{' '}
          {opacity}%
        </label>
      </div>
      {cadError && (
        <div role="alert">
          {cadError}{' '}
          <button type="button" onClick={() => setCadRetry((v) => v + 1)}>
            Retry CAD
          </button>
        </div>
      )}
      <div className="cc-compound__details" aria-live="polite">
        <span className="cc-compound__hover">
          {hoveredFeature
            ? `${slotLabel(hoveredFeature)} · ${isMapped(hoveredFeature) ? 'Mapped' : 'Unresolved'}${simulation ? (vehicles.has(featureId(hoveredFeature)) ? ` · Occupied (simulated): ${vehicles.get(featureId(hoveredFeature))!.make} ${vehicles.get(featureId(hoveredFeature))!.model} · ${vehicles.get(featureId(hoveredFeature))!.colour} · ${vehicles.get(featureId(hoveredFeature))!.registration} · ${vehicles.get(featureId(hoveredFeature))!.id} · ${vehicles.get(featureId(hoveredFeature))!.vin}` : ' · Empty (simulation)') : ''}`
            : 'Hover a candidate for details. Click to keep its selection.'}
          {mode === 'recorded' &&
            hoveredFeature &&
            ` · ${recordedStatus(recorded.rows.get(hoveredFeature.properties.slotId))}`}
        </span>
        {selectedFeature && (
          <>
            <strong>
              {slotLabel(selectedFeature)} ·{' '}
              {isMapped(selectedFeature)
                ? 'Mapped source identity'
                : 'Unresolved source identity'}
            </strong>
            {selectedFeature.properties.drawingReference && (
              <span>
                Drawing reference only; formal source identity is not linked.
              </span>
            )}
            <span>
              Review {selectedFeature.properties.reviewId} · Block/lane{' '}
              {[
                selectedFeature.properties.block,
                selectedFeature.properties.lane,
              ]
                .filter(Boolean)
                .join(' / ') || 'Not linked'}{' '}
              · Number {selectedFeature.properties.number ?? 'Not linked'} ·
              Source customer{' '}
              {selectedFeature.properties.sourceCustomerId ?? 'Not linked'} ·
              Source location{' '}
              {selectedFeature.properties.sourceLocationId ?? 'Not linked'}
            </span>
            <span>
              Candidate centre{' '}
              {Number(selectedFeature.properties.latitude).toFixed(6)},{' '}
              {Number(selectedFeature.properties.longitude).toFixed(6)} ·{' '}
              {selectedFeature.properties.coordinateStatus ||
                'candidate_requires_remote_controls'}{' '}
              · Slot {featureId(selectedFeature)}
            </span>
            {metadata.historicalIdentityReviews
              ?.filter(
                (review) =>
                  review.reviewId === selectedFeature.properties.reviewId,
              )
              .map((review) => (
                <span key={review.reviewId} className="cc-compound__notice">
                  Historical customer conflict:{' '}
                  {review.classification?.replace(/_/g, ' ')} (
                  {review.comparisonDate}). Source preserved; not rechecked by
                  this import.
                </span>
              ))}
            {simulation && (
              <span className="cc-compound__simulation">
                {vehicles.has(selected!)
                  ? `Occupied — SIMULATED ${vehicles.get(selected!)!.make} ${vehicles.get(selected!)!.model} · ${vehicles.get(selected!)!.colour} · ${vehicles.get(selected!)!.registration} · ${vehicles.get(selected!)!.id} · VIN ${vehicles.get(selected!)!.vin}`
                  : 'Empty — SIMULATION'}
              </span>
            )}
            {mode === 'recorded' && (
              <RecordedVehicleDetails
                row={recorded.rows.get(selectedFeature.properties.slotId)}
                positionSource={recorded.snapshot?.positionSource}
              />
            )}
            <button type="button" onClick={() => adapter.current?.select(null)}>
              Clear selection
            </button>
          </>
        )}
      </div>
    </MapSettings>
  );
}
